/* ==========================================================================
   34 MANSION 디자인 토큰
   design_handoff_34mansion/tokens.css 의 :root 와 1:1 로 대응합니다.
   - CSS 변수 선언은 mansionRootVars 로 만들어져 GlobalStyles 에서 :root 에 주입
   - styled 컴포넌트에서는 m.color.navy 처럼 var() 참조를 사용
   - Auction34 와 같은 구조지만 변수 접두어가 --m- 이라 서로 충돌하지 않습니다.
   ========================================================================== */

import { HouseKey } from '@/components/Mansion/types';

type TokenDef = { css: string; value: string; use?: string };

/** 키 리터럴 타입을 유지하기 위한 항등 함수 */
const group = <T extends Record<string, TokenDef>>(defs: T) => defs;

const surface = group({
  heroBg: {
    css: '--m-hero-bg',
    value: '#0E1830',
    use: '문서 배경. 로드 시 흰 화면이 한 프레임 지나가는 것을 막습니다.',
  },
  bg: { css: '--m-bg', value: '#FAF9F6', use: '페이지 기본 배경' },
  surface: { css: '--m-surface', value: '#FFFFFF', use: '카드 표면' },
  surfaceAlt: { css: '--m-surface-alt', value: '#F6F3EC', use: '푸터 배경' },
  border: { css: '--m-border', value: '#E9E4DA' },
  borderHover: { css: '--m-border-hover', value: '#D9D0BC' },
  rule: { css: '--m-rule', value: '#D9D3C7' },
});

const ink = group({
  navy: { css: '--m-navy', value: '#16233C', use: '텍스트·히어로' },
  text: { css: '--m-text', value: '#1B1B1B' },
  textBody: { css: '--m-text-body', value: '#4A463F' },
  textMuted: { css: '--m-text-muted', value: '#7A7671' },
  textFaint: { css: '--m-text-faint', value: '#9C9689' },
});

/** 악센트 — 아껴서 씁니다. */
const accent = group({
  purple: { css: '--m-purple', value: '#4B3A8F', use: 'CTA · active 만' },
  gold: {
    css: '--m-gold',
    value: '#C6A96B',
    use: '얇은 선, 작은 표식, hover 만',
  },
});

/** 03-B 명부 미확인 화면 전용 중립 팔레트 */
const neutral = group({
  bg: { css: '--m-neutral-bg', value: '#EFEEEC' },
  ink: { css: '--m-neutral-ink', value: '#20211F' },
  sub: { css: '--m-neutral-sub', value: '#5C5A58' },
  label: { css: '--m-neutral-label', value: '#7C7A78' },
});

/**
 * MANSION MASTER 화면 전용 팔레트 (PORTAL-13659).
 * 03-B 와 같은 자리에 뜨지만 방향이 정반대입니다 — 중립 팔레트는 아이보리 위의
 * 어두운 글자이고, 이쪽은 어두운 영상 위의 밝은 글자입니다.
 */
const master = group({
  ink: {
    css: '--m-master-ink',
    value: '#F6F2EA',
    use: 'MANSION MASTER 제목. 순백이 아닌 아이보리 — 영상 위에서 덜 튑니다.',
  },
  gold: {
    css: '--m-master-gold',
    value: '#E6D3AA',
    use: '제목 아래 금선. accent.gold 보다 밝습니다(어두운 배경 기준).',
  },
  veil: {
    css: '--m-master-veil',
    value: 'rgba(8, 14, 22, .58)',
    use: '카피 뒤 타원 halo 의 중심 색',
  },
});

const houses = group({
  azure: { css: '--m-azure', value: '#10508F' },
  emerald: { css: '--m-emerald', value: '#0F5A33' },
  rose: {
    css: '--m-rose',
    value: '#D96C93',
    use: '빨강이 아니라 핑크 — 드레스 코드가 PINK 입니다.',
  },
  white: { css: '--m-white', value: '#B0B4B8' },
  walnut: { css: '--m-walnut', value: '#7A4A1E' },
  onyx: { css: '--m-onyx', value: '#262626' },
  silver: { css: '--m-silver', value: '#7E8894' },
  crimson: { css: '--m-crimson', value: '#A81C2E' },
  golden: { css: '--m-golden', value: '#D4A017' },
  burgundy: { css: '--m-burgundy', value: '#7A1030' },
});

const type = group({
  serif: { css: '--m-serif', value: "'Cormorant Garamond', serif" },
  sans: { css: '--m-sans', value: 'Pretendard, -apple-system, sans-serif' },

  title: {
    css: '--m-title',
    value: 'clamp(38px, 5.2vw, 72px)',
    use: '페이지 타이틀, weight 500',
  },
  section: { css: '--m-section', value: 'clamp(30px, 3.8vw, 50px)' },
  house: {
    css: '--m-house',
    value: 'clamp(38px, 4.4vw, 68px)',
    use: 'reveal 하우스명',
  },
  body: { css: '--m-body', value: '15px' },
  small: { css: '--m-small', value: '12.5px' },
  label: { css: '--m-label', value: '11px', use: '대문자, .3em 자간' },
});

const layout = group({
  max: { css: '--m-max', value: '1240px' },
  gutter: { css: '--m-gutter', value: '44px', use: '820px 이하에서 20px' },
  sectionGap: { css: '--m-section-gap', value: '64px' },
  cardPad: { css: '--m-card-pad', value: '30px 32px' },
});

const motion = group({
  ease: { css: '--m-ease', value: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  fast: { css: '--m-fast', value: '240ms' },
  base: { css: '--m-base', value: '320ms' },
  slow: { css: '--m-slow', value: '520ms' },
  /** 시스템에서 쓰는 유일한 그림자 */
  shadow: { css: '--m-shadow', value: '0 18px 40px rgba(22, 35, 60, .14)' },
});

const toVars = <T extends Record<string, TokenDef>>(defs: T) => {
  const vars = {} as Record<keyof T, string>;
  (Object.keys(defs) as (keyof T)[]).forEach((key) => {
    vars[key] = `var(${defs[key].css})`;
  });
  return vars;
};

/** styled 컴포넌트에서 쓰는 var() 참조 모음 */
export const m = {
  surface: toVars(surface),
  ink: toVars(ink),
  accent: toVars(accent),
  neutral: toVars(neutral),
  master: toVars(master),
  house: toVars(houses) as Record<HouseKey, string>,
  type: toVars(type),
  layout: toVars(layout),
  motion: toVars(motion),
};

/** :root 에 주입할 CSS 변수 선언문 */
export const mansionRootVars = (
  [
    surface,
    ink,
    accent,
    neutral,
    master,
    houses,
    type,
    layout,
    motion,
  ] as Record<string, TokenDef>[]
)
  .reduce<TokenDef[]>(
    (acc, defs) => acc.concat(Object.keys(defs).map((key) => defs[key])),
    [],
  )
  .map((token) => `${token.css}: ${token.value};`)
  .join('\n    ');

/**
 * 레이아웃 규칙.
 * border-radius 는 시스템 전면 0 이라 토큰 자체가 없습니다 — 값을 새로 만들지 마세요.
 */
export const mansionLayout = {
  maxWidth: '1240px',
  gutter: '44px',
  gutterMobile: '20px',
  /** 820px 이하 = 1단 레이아웃 */
  mobileBreakpoint: 820,
  headerHeight: '70px',
} as const;

/** 미디어쿼리 헬퍼. */
export const mansionMq = {
  mobile: `@media (max-width: ${mansionLayout.mobileBreakpoint}px)`,
  /**
   * 821~1150px. 1단으로 접히기 전에 2단 그리드만 먼저 접는 구간입니다.
   * 목업의 `@media(min-width:821px) and (max-width:1150px)` 규칙으로,
   * 이 폭에서는 360px 하우스 카드와 우측 2열이 서로 눌려 카드가 먼저 깨집니다.
   */
  narrow: `@media (min-width: 821px) and (max-width: 1150px)`,
} as const;
